import { useEffect, useState } from 'react';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';

export default function ContactPage() {
  const [isVisible, setIsVisible] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');

  useEffect(() => {
    setIsVisible(true);
    window.scrollTo(0, 0);
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitStatus('sending');

    // Simulate form submission
    setTimeout(() => {
      setSubmitStatus('success');
      setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
      setTimeout(() => setSubmitStatus('idle'), 3000);
    }, 1500);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const contactInfo = [
    {
      icon: 'ri-mail-line',
      title: 'E-posta',
      content: 'tetra@university.edu',
      link: 'mailto:tetra@university.edu',
      color: 'bg-[#00d9ff]/10 text-[#00d9ff]'
    },
    {
      icon: 'ri-phone-line',
      title: 'Telefon',
      content: '+90 555 123 45 67',
      link: 'tel:+905551234567',
      color: 'bg-[#ff6b35]/10 text-[#ff6b35]'
    },
    {
      icon: 'ri-map-pin-line',
      title: 'Adres',
      content: 'Üniversite Kampüsü, Mühendislik Fakültesi, İstanbul',
      link: '#',
      color: 'bg-[#ffd700]/10 text-[#ffd700]'
    },
    {
      icon: 'ri-time-line',
      title: 'Çalışma Saatleri',
      content: 'Pazartesi - Cuma: 09:00 - 18:00',
      link: '#',
      color: 'bg-white/10 text-white'
    }
  ];

  const socialMedia = [
    { icon: 'ri-instagram-fill', name: 'Instagram', link: '#', color: 'hover:bg-[#E4405F]' },
    { icon: 'ri-linkedin-fill', name: 'LinkedIn', link: '#', color: 'hover:bg-[#0077B5]' },
    { icon: 'ri-youtube-fill', name: 'YouTube', link: '#', color: 'hover:bg-[#FF0000]' },
    { icon: 'ri-twitter-fill', name: 'Twitter', link: '#', color: 'hover:bg-[#1DA1F2]' },
    { icon: 'ri-github-fill', name: 'GitHub', link: '#', color: 'hover:bg-white' }
  ];

  return (
    <div className="min-h-screen bg-black text-white">
      <Navbar />

      {/* Hero Section */}
      <section className="relative h-[60vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=modern%20engineering%20office%20workspace%20communication%20technology%20dark%20industrial%20background%20professional%20environment%20contact%20center%20dramatic%20lighting%20minimalist%20design&width=1920&height=800&seq=contact-hero&orientation=landscape"
            alt="Contact Hero"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black"></div>
        </div>

        <div className={`relative z-10 text-center px-8 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <h3 className="text-[#00d9ff] text-sm uppercase tracking-[0.3em] font-semibold mb-4">
            İLETİŞİM
          </h3>
          <h1 className="text-7xl font-bold mb-6">Bize Ulaşın</h1>
          <p className="text-2xl text-white/70 italic max-w-2xl mx-auto">
            Sorularınız, önerileriniz veya iş birliği teklifleriniz için buradayız
          </p>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border-2 border-white/30 rounded-full flex justify-center pt-2">
            <div className="w-1 h-3 bg-[#00d9ff] rounded-full animate-pulse"></div>
          </div>
        </div>
      </section>

      {/* Contact Info Cards */}
      <section className="py-20 px-8 bg-[#0a0a0a]">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {contactInfo.map((info, index) => (
              <a
                key={index}
                href={info.link}
                className={`group p-8 bg-black/50 border border-[#333] rounded-lg hover:border-[#00d9ff] transition-all duration-500 cursor-pointer ${
                  isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                }`}
                style={{ transitionDelay: `${index * 0.1}s` }}
              >
                <div className={`w-14 h-14 flex items-center justify-center rounded-full mb-6 ${info.color} group-hover:scale-110 transition-transform duration-300`}>
                  <i className={`${info.icon} text-2xl`}></i>
                </div>
                <h3 className="text-lg font-bold mb-3">{info.title}</h3>
                <p className="text-white/60 leading-relaxed">{info.content}</p>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form & Map */}
      <section className="py-32 px-8 bg-black">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Contact Form */}
            <div className={`transition-all duration-1000 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'}`}>
              <h2 className="text-4xl font-bold mb-4">Mesaj Gönderin</h2>
              <p className="text-white/60 mb-10">
                Formu doldurarak bize ulaşabilirsiniz. En kısa sürede size geri dönüş yapacağız.
              </p>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="name" className="block text-sm font-semibold mb-2 text-white/80">
                      Ad Soyad *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="w-full px-5 py-4 bg-[#1a1a1a] border border-[#333] rounded-lg text-white placeholder-white/40 focus:outline-none focus:border-[#00d9ff] transition-colors duration-300"
                      placeholder="Adınız ve soyadınız"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm font-semibold mb-2 text-white/80">
                      E-posta *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full px-5 py-4 bg-[#1a1a1a] border border-[#333] rounded-lg text-white placeholder-white/40 focus:outline-none focus:border-[#00d9ff] transition-colors duration-300"
                      placeholder="ornek@email.com"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="phone" className="block text-sm font-semibold mb-2 text-white/80">
                      Telefon
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className="w-full px-5 py-4 bg-[#1a1a1a] border border-[#333] rounded-lg text-white placeholder-white/40 focus:outline-none focus:border-[#00d9ff] transition-colors duration-300"
                      placeholder="+90 555 123 45 67"
                    />
                  </div>
                  <div>
                    <label htmlFor="subject" className="block text-sm font-semibold mb-2 text-white/80">
                      Konu *
                    </label>
                    <select
                      id="subject"
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      required
                      className="w-full px-5 py-4 bg-[#1a1a1a] border border-[#333] rounded-lg text-white focus:outline-none focus:border-[#00d9ff] transition-colors duration-300 cursor-pointer"
                    >
                      <option value="">Konu seçiniz</option>
                      <option value="genel">Genel Bilgi</option>
                      <option value="sponsorluk">Sponsorluk</option>
                      <option value="takım">Takıma Katılım</option>
                      <option value="medya">Medya & Basın</option>
                      <option value="diger">Diğer</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-semibold mb-2 text-white/80">
                    Mesajınız *
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={6}
                    maxLength={500}
                    className="w-full px-5 py-4 bg-[#1a1a1a] border border-[#333] rounded-lg text-white placeholder-white/40 focus:outline-none focus:border-[#00d9ff] transition-colors duration-300 resize-none"
                    placeholder="Mesajınızı buraya yazın... (Maksimum 500 karakter)"
                  />
                  <div className="text-right text-sm text-white/40 mt-2">
                    {formData.message.length}/500
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={submitStatus === 'sending'}
                  className="w-full px-10 py-4 bg-[#00d9ff] text-black font-bold rounded-full hover:bg-[#00b8d4] transition-all duration-300 hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
                >
                  {submitStatus === 'sending' ? 'Gönderiliyor...' : 'Mesaj Gönder'}
                </button>

                {submitStatus === 'success' && (
                  <div className="p-4 bg-green-500/10 border border-green-500/30 rounded-lg text-green-400 text-center">
                    Mesajınız başarıyla gönderildi! En kısa sürede size dönüş yapacağız.
                  </div>
                )}
              </form>
            </div>

            {/* Map & Social */}
            <div className={`space-y-8 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'}`}>
              {/* Map */}
              <div className="h-[400px] rounded-lg overflow-hidden border border-[#333]">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3009.7547!2d28.9784!3d41.0082!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zNDHCsDAwJzI5LjUiTiAyOMKwNTgnNDIuMiJF!5e0!3m2!1str!2str!4v1234567890"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="TETRA Location"
                ></iframe>
              </div>

              {/* Social Media */}
              <div className="p-8 bg-[#1a1a1a] border border-[#333] rounded-lg">
                <h3 className="text-2xl font-bold mb-6">Sosyal Medyada Bizi Takip Edin</h3>
                <p className="text-white/60 mb-8">
                  Yarışlarımız, projelerimiz ve takım aktivitelerimizden haberdar olmak için sosyal medya hesaplarımızı takip edin.
                </p>
                <div className="flex flex-wrap gap-4">
                  {socialMedia.map((social, index) => (
                    <a
                      key={index}
                      href={social.link}
                      className={`flex items-center gap-3 px-6 py-3 bg-black border border-[#333] rounded-full transition-all duration-300 ${social.color} hover:scale-105 cursor-pointer group whitespace-nowrap`}
                    >
                      <i className={`${social.icon} text-xl text-white group-hover:text-black`}></i>
                      <span className="font-semibold group-hover:text-black">{social.name}</span>
                    </a>
                  ))}
                </div>
              </div>

              {/* FAQ Quick Links */}
              <div className="p-8 bg-[#1a1a1a] border border-[#333] rounded-lg">
                <h3 className="text-2xl font-bold mb-6">Sık Sorulan Sorular</h3>
                <div className="space-y-4">
                  {[
                    { q: 'Takıma nasıl katılabilirim?', a: 'Başvuru formunu doldurarak takımımıza katılabilirsiniz.' },
                    { q: 'Sponsor olmak için ne yapmalıyım?', a: 'Sponsorluk konusunda detaylı bilgi için bizimle iletişime geçin.' },
                    { q: 'Yarışları izleyebilir miyim?', a: 'Evet, yarış tarihlerini sosyal medyadan duyuruyoruz.' }
                  ].map((faq, index) => (
                    <details key={index} className="group cursor-pointer">
                      <summary className="flex items-center justify-between p-4 bg-black/50 rounded-lg hover:bg-black transition-colors duration-300">
                        <span className="font-semibold">{faq.q}</span>
                        <i className="ri-arrow-down-s-line text-xl group-open:rotate-180 transition-transform duration-300"></i>
                      </summary>
                      <p className="p-4 text-white/60 leading-relaxed">{faq.a}</p>
                    </details>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
